import type { LeadAddress } from '../data/types';
import { getStateLabel, isIndiaAddress } from '../data/addressRegions';
import { formatUSPhone } from './validationHelpers';
import { stripCallingCode, toE164FromCallingCode } from './countryCodes';

/**
 * Parse / format / validate helpers for the structured catering address
 * (`LeadAddress`) used by leads, estimates and orders.
 *
 * Everything here tolerates the legacy single-line address: records created
 * before the structured form shipped hold a plain string, which parses into
 * `addressLine1` so old leads and orders keep rendering and stay editable.
 */

/** The postal subfields, in the order they are rendered and printed. */
export const POSTAL_FIELDS = [
  'addressLine1',
  'addressLine2',
  'landmark',
  'city',
  'district',
  'state',
  'zipcode',
] as const;

/** Delivery-contact subfields — who receives the order, not where it goes. */
export const CONTACT_FIELDS = ['contactName', 'contactPhone'] as const;

/** Every subfield carried in the address JSON. */
export const ADDRESS_FIELDS = [...POSTAL_FIELDS, ...CONTACT_FIELDS] as const;

export type AddressField = (typeof ADDRESS_FIELDS)[number];

/** Per-field character caps. Sized to fit the backend's address JSON comfortably. */
export const ADDRESS_LIMITS: Record<AddressField, number> = {
  addressLine1: 120,
  addressLine2: 120,
  landmark: 80,
  city: 60,
  district: 60,
  state: 60,
  zipcode: 10,
  contactName: 60,
  /** Local digits as typed — "(555) 123-4567" is 14 chars. */
  contactPhone: 14,
};

export const EMPTY_ADDRESS: LeadAddress = {};

function pickAddress(src: Record<string, unknown>): LeadAddress {
  const out: LeadAddress = {};
  for (const key of ADDRESS_FIELDS) {
    const val = src[key];
    if (typeof val === 'string' && val.trim()) out[key] = val.trim();
  }
  return out;
}

/**
 * Normalises whatever the API (or an older record) hands back into a
 * `LeadAddress`. Accepts an object, a JSON string, or a legacy single-line
 * string — the last of which lands in `addressLine1` so nothing is lost.
 */
export function parseAddress(raw: unknown): LeadAddress {
  if (!raw) return { ...EMPTY_ADDRESS };

  if (typeof raw === 'object') {
    return pickAddress(raw as Record<string, unknown>);
  }

  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return { ...EMPTY_ADDRESS };
    if (trimmed.startsWith('{')) {
      try {
        const parsed: unknown = JSON.parse(trimmed);
        if (parsed && typeof parsed === 'object') {
          return pickAddress(parsed as Record<string, unknown>);
        }
      } catch {
        // Not valid JSON after all — fall through to the legacy single-line path.
      }
    }
    return { addressLine1: trimmed };
  }

  return { ...EMPTY_ADDRESS };
}

/**
 * True when no *postal* subfield carries a value. Deliberately ignores the
 * delivery contact: a contact name alone is not a delivery address, and this is
 * what gates the "address required for delivery orders" rule.
 */
export function isAddressEmpty(address: LeadAddress | null | undefined): boolean {
  if (!address) return true;
  return POSTAL_FIELDS.every((f) => !(address[f] ?? '').trim());
}

/** True when neither delivery-contact field carries a value. */
export function isContactEmpty(address: LeadAddress | null | undefined): boolean {
  if (!address) return true;
  return CONTACT_FIELDS.every((f) => !(address[f] ?? '').trim());
}

/**
 * Trims every subfield and drops the empty ones. Returns `undefined` for a
 * fully empty address so callers can omit the key from a request payload.
 */
export function trimAddress(address: LeadAddress | null | undefined): LeadAddress | undefined {
  if (!address) return undefined;
  const out = pickAddress(address as unknown as Record<string, unknown>);
  return Object.keys(out).length > 0 ? out : undefined;
}

/**
 * The address as display lines, in postal order. India keeps district on the
 * locality line; the US collapses to "City, ST 60601".
 */
export function formatAddressLines(
  address: LeadAddress | null | undefined,
  country?: string | null,
): string[] {
  if (isAddressEmpty(address)) return [];
  const a = address as LeadAddress;
  const lines: string[] = [];

  if (a.addressLine1) lines.push(a.addressLine1);
  if (a.addressLine2) lines.push(a.addressLine2);
  if (a.landmark) lines.push(`Near ${a.landmark}`);

  const stateLabel = getStateLabel(country, a.state ?? '');

  if (isIndiaAddress(country)) {
    // Chennai, Chennai District
    const locality = [a.city, a.district].filter(Boolean).join(', ');
    if (locality) lines.push(locality);
    // Tamil Nadu 600017
    const region = [stateLabel, a.zipcode].filter(Boolean).join(' ');
    if (region) lines.push(region);
  } else {
    // Chicago, IL 60601 — USPS keeps the 2-letter code, not the full name.
    const region = [a.city, [a.state, a.zipcode].filter(Boolean).join(' ')]
      .filter(Boolean)
      .join(', ');
    if (region) lines.push(region);
  }

  return lines;
}

/** The address on one line, for tables, chips and single-row detail fields. */
export function formatAddress(
  address: LeadAddress | null | undefined,
  country?: string | null,
  fallback = '',
): string {
  const lines = formatAddressLines(address, country);
  return lines.length > 0 ? lines.join(', ') : fallback;
}

/** Label for the postal-code field — "Pincode" in India, "Zipcode" elsewhere. */
export function zipcodeLabel(country?: string | null): string {
  return isIndiaAddress(country) ? 'Pincode' : 'Zipcode';
}

const INDIA_PIN_RE = /^\d{6}$/;
const US_ZIP_RE = /^\d{5}(-\d{4})?$/;

/**
 * Per-country required-field and format rules, matching the Figma:
 * Address Line 1, City, State and the postal code are required; District is
 * required for India only; Address Line 2 and Landmark are always optional.
 *
 * Pass `required = false` for the lead form, where the address block as a whole
 * is optional and a half-known address (city only, say) is normal early in the
 * funnel. Format rules still apply to whatever the user did fill in.
 */
export function validateAddress(
  address: LeadAddress | null | undefined,
  country?: string | null,
  required = true,
): Partial<Record<AddressField, string>> {
  const errors: Partial<Record<AddressField, string>> = {};
  const a = address ?? {};
  const india = isIndiaAddress(country);
  const zip = (a.zipcode ?? '').trim();

  if (required) {
    if (!(a.addressLine1 ?? '').trim()) errors.addressLine1 = 'Address line 1 is required';
    if (!(a.city ?? '').trim()) errors.city = 'City is required';
    if (india && !(a.district ?? '').trim()) errors.district = 'District is required';
    if (!(a.state ?? '').trim()) errors.state = 'State is required';
    if (!zip) errors.zipcode = `${zipcodeLabel(country)} is required`;
  }

  // Format is checked whenever a postal code is present, required or not.
  if (zip && !errors.zipcode) {
    if (india && !INDIA_PIN_RE.test(zip)) {
      errors.zipcode = 'Enter a valid 6-digit pincode';
    } else if (!india && !US_ZIP_RE.test(zip)) {
      errors.zipcode = 'Enter a valid ZIP code, e.g. 60601';
    }
  }

  // Delivery contact is optional on every screen — the customer's own phone is
  // already captured — but a number that was typed must be a complete one.
  const contactDigits = (a.contactPhone ?? '').replace(/\D/g, '');
  if (contactDigits && contactDigits.length !== 10) {
    errors.contactPhone = india
      ? 'Enter a valid 10-digit mobile number'
      : 'Enter a valid 10-digit phone number';
  }

  return errors;
}

/** Stable key for dirty-tracking and edit-unchanged comparisons. */
export function addressSnapshot(address: LeadAddress | null | undefined): string {
  const a = address ?? {};
  return ADDRESS_FIELDS.map((f) => (a[f] ?? '').trim()).join('|');
}

/**
 * Local display formatting for the delivery-contact phone. US outlets get the
 * familiar "(555) 123-4567" punctuation; everywhere else the digits are left
 * alone, since US grouping is wrong for a 10-digit Indian mobile.
 */
export function formatLocalPhone(raw: string, country?: string | null): string {
  if (isIndiaAddress(country)) return raw.replace(/\D/g, '').slice(0, 10);
  return formatUSPhone(raw);
}

/**
 * API shape -> form state. Parses the stored address and converts the contact
 * phone from `+91-9876543210` back to local digits for editing, mirroring what
 * the customer-phone fields already do with `stripCallingCode`.
 */
export function addressForForm(
  raw: unknown,
  callingCode: string,
  country?: string | null,
): LeadAddress {
  const parsed = parseAddress(raw);
  if (!parsed.contactPhone) return parsed;
  return {
    ...parsed,
    contactPhone: formatLocalPhone(stripCallingCode(parsed.contactPhone, callingCode), country),
  };
}

/**
 * Form state -> API shape. Trims every field, drops the empties, and puts the
 * contact phone back into the `+<code>-<digits>` form the backend stores.
 * Returns `undefined` for a wholly empty address so callers can omit the key.
 */
export function addressForSubmit(
  address: LeadAddress | null | undefined,
  callingCode: string,
): LeadAddress | undefined {
  const trimmed = trimAddress(address);
  if (!trimmed) return undefined;
  if (!trimmed.contactPhone) return trimmed;
  const e164 = toE164FromCallingCode(trimmed.contactPhone, callingCode);
  if (!e164) {
    const { contactPhone, ...rest } = trimmed;
    return Object.keys(rest).length > 0 ? rest : undefined;
  }
  return { ...trimmed, contactPhone: e164 };
}
